<template>
  <div :class="$style.container" :style="{ paddingBottom: props.hasActionSection ? '0' : '1rem' }">
    <div v-if="props.infoMessage" :class="$style.infoMessage">
      <VdIcon store="fas" name="circle-info" color="black" />
      <span>{{ infoMessage }}</span>
    </div>
    <div :class="$style.inputWrapper">
      <InputText
        v-if="props.enableSearch"
        ref="inputRef"
        v-model="newItem"
        :placeholder="`Search ${props.hasAddSection ? 'or add a new ' : ''}item`"
        :class="$style.input"
        @keyup.enter="emitAddItem"
      />
      <Button v-if="props.enableSearch" icon="pi pi-check" @click="emitAddItem"></Button>
    </div>
    <div :class="$style.list">
      <div v-for="(item, index) in sortedFilteredItems" :key="item.name" :class="$style.listItem">
        <div :class="$style.itemWrapper">
          <Chip
            :label="item.selected ? `✓ ${item.name}` : item.name"
            :class="[$style.chip, $style.limitedChip, item.selected ? $style.selectedChip : '']"
            @click="emitToggleSelection(item)"
          />
          <VdIcon
            v-if="props.allowDeletion && item.isDeletable"
            store="fas"
            name="trash"
            :class="$style.deleteBtn"
            custom-size="20px"
            @click="emitDeleteItem(item)"
          />
        </div>
        <div v-if="showSelectedItemBorder(index)">
          <div :class="$style.selectedItemBorder"></div>
        </div>
      </div>
    </div>
    <div v-if="isNewItem" :class="$style.addNewItem">
      {{ props.hasAddSection ? "Item not found, press enter to add it" : "Item not found" }}
    </div>
    <div v-if="props.hasActionSection" :class="$style.actionRow">
      <Button icon="pi pi-times" severity="secondary" outlined @click="discardChanges" />
      <Button icon="pi pi-check" :disabled="selectedItems.length < props.nbOfItemsRequired" @click="saveChanges" />
    </div>
    <div v-if="selectedItems?.length < props.nbOfItemsRequired" :class="$style.invalidState">
      <p>{{ props.validationMessage }}</p>
    </div>
  </div>
</template>

<script setup>
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import Button from "primevue/button";
import Chip from "primevue/chip";
import InputText from "primevue/inputtext";
import { computed, ref } from "vue";

const props = defineProps({
  items: Array,
  selectedItems: Array,
  enableSearch: {
    type: Boolean,
    default: true,
  },
  hasAddSection: {
    type: Boolean,
    default: true,
  },
  allowDeletion: {
    type: Boolean,
    default: true,
  },
  hasActionSection: {
    type: Boolean,
    default: false,
  },
  nbOfItemsRequired: {
    type: Number,
    default: 0,
  },
  selectionKey: {
    type: String,
    default: "name",
  },
  validationMessage: {
    type: String,
    default: "at least one segment should be selected",
  },
  infoMessage: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["addItem", "deleteItem", "toggleSelection", "saveChanges", "discardChanges"]);
const newItem = ref("");

const isNewItem = computed(
  () => newItem.value && !props.items.some((item) => item.name.toLowerCase() === newItem.value.toLowerCase())
);

const sortedFilteredItems = computed(() => {
  const result = props.items
    .map((item) => ({
      ...item,
      selected: props.selectedItems.some((selected) => selected[props.selectionKey] === item[props.selectionKey]),
    }))
    .filter((item) => item.name.toLowerCase().includes(newItem.value.toLowerCase()));

  // eslint-disable-next-line no-nested-ternary
  result.sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1));
  return result;
});

const selectedItemCount = computed(() => props.selectedItems.length);

const showSelectedItemBorder = computed(() => (index) => {
  const isLastSelectedItem = index === selectedItemCount.value - 1 && selectedItemCount.value > 0;
  const isLastItemInList =
    !props.hasAddSection && index === selectedItemCount.value - 1 && index !== props.items.length - 1;
  return (isLastSelectedItem && props.hasAddSection) || isLastItemInList;
});

function emitAddItem() {
  if (isNewItem.value) {
    emit("addItem", newItem.value.trim());
    newItem.value = "";
  }
}

function emitToggleSelection(item) {
  emit("toggleSelection", item);
}

function emitDeleteItem(item) {
  emit("deleteItem", item);
}

function discardChanges() {
  emit("discardChanges");
}

function saveChanges() {
  emit("saveChanges");
}
</script>

<style module lang="scss">
.container {
  background: white;
  z-index: 1;
}

.inputWrapper {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 1rem;
}

.input {
  width: 100%;
}

.list {
  max-height: 200px;
  overflow-y: auto;
}

.listItem {
  padding: 0.4rem 1rem 0 1rem;
  align-items: center;
  cursor: pointer;
}

.chip {
  margin-right: 5px;

  &:hover {
    background-color: var(--vd-color-blue-light-6);
  }

  &.selectedChip {
    background-color: var(--vd-color-blue-default);
    color: white;

    &:hover {
      background-color: var(--vd-color-blue-dark-2);
    }
  }
}

.selectedItemBorder {
  border-bottom: 2px solid #ccc;
  margin: 5px 0;
  width: 100%;
}

.addNewItem {
  text-align: center;
  padding: 5px;
}

.itemWrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.deleteBtn {
  cursor: pointer;
}
.actionRow {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
}
.invalidState {
  display: flex;
  justify-content: flex-end;
  color: red;
  font-size: 12px;
}
.limitedChip div {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.infoMessage {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin: 1rem;
  margin-bottom: 0;
  border-radius: 0.5rem;
  background-color: var(--vd-color-blue-light-9);
  font-size: 12px;

  svg {
    margin-right: 0.75rem;
  }
}
</style>
